/**
 * HIPAA Compliance Utilities
 * Data masking, sanitization, and PHI/PII protection for analytics tracking
 */

// ============================================================================
// Sensitive Data Detection
// ============================================================================

/**
 * Patterns to detect PHI/PII in strings
 */
const PHI_PATTERNS = {
  // Social Security Numbers
  ssn: /\b\d{3}-\d{2}-\d{4}\b|\b\d{9}\b/g,

  // Phone numbers
  phone: /(\+\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,

  // Email addresses
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,

  // Dates of birth (various formats)
  dob: /\b\d{1,2}[-/]\d{1,2}[-/]\d{2,4}\b/g,

  // Medical Record Numbers (MRN)
  mrn: /\b(MRN|mrn|Medical Record|medical record)[:\s]*\d{5,10}\b/gi,

  // Medicaid/Medicare IDs
  medicaidId: /\b\d{10,12}\b/g,

  // Credit card numbers
  creditCard: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,

  // IP addresses
  ipAddress: /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,

  // Street addresses (basic pattern)
  address: /\b\d+\s+[A-Za-z\s]+(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Circle|Cir|Way)\b/gi,
};

/**
 * Check if a string contains PHI/PII
 */
export function containsPHI(text: string): boolean {
  if (!text) return false;

  for (const pattern of Object.values(PHI_PATTERNS)) {
    if (pattern.test(text)) {
      return true;
    }
  }

  return false;
}

/**
 * Sanitize text by removing PHI/PII
 */
export function sanitizeText(text: string): string {
  if (!text) return text;

  let sanitized = text;

  // Replace SSN
  sanitized = sanitized.replace(PHI_PATTERNS.ssn, '[SSN REDACTED]');

  // Replace phone numbers
  sanitized = sanitized.replace(PHI_PATTERNS.phone, '[PHONE REDACTED]');

  // Replace emails
  sanitized = sanitized.replace(PHI_PATTERNS.email, '[EMAIL REDACTED]');

  // Replace DOB
  sanitized = sanitized.replace(PHI_PATTERNS.dob, '[DOB REDACTED]');

  // Replace MRN
  sanitized = sanitized.replace(PHI_PATTERNS.mrn, '[MRN REDACTED]');

  // Replace Medicaid IDs
  sanitized = sanitized.replace(PHI_PATTERNS.medicaidId, '[ID REDACTED]');

  // Replace credit cards
  sanitized = sanitized.replace(PHI_PATTERNS.creditCard, '[CARD REDACTED]');

  // Replace IP addresses
  sanitized = sanitized.replace(PHI_PATTERNS.ipAddress, '[IP REDACTED]');

  // Replace addresses
  sanitized = sanitized.replace(PHI_PATTERNS.address, '[ADDRESS REDACTED]');

  return sanitized;
}

// ============================================================================
// Property Sanitization for Analytics
// ============================================================================

/**
 * List of property keys that contain PHI/PII
 */
const SENSITIVE_KEYS = [
  'ssn',
  'social_security',
  'phone',
  'phone_number',
  'email',
  'email_address',
  'dob',
  'date_of_birth',
  'birthdate',
  'address',
  'street_address',
  'medicaid_id',
  'medicare_id',
  'medical_record_number',
  'mrn',
  'patient_id',
  'member_id',
  'ip_address',
  'credit_card',
  'password',
  'api_key',
  'token',
  'secret',
];

/**
 * Check if a property key is sensitive
 */
function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_KEYS.some(sensitiveKey => lowerKey.includes(sensitiveKey));
}

/**
 * Sanitize properties object for analytics tracking
 */
export function sanitizeProperties(properties: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(properties)) {
    // Skip sensitive keys entirely
    if (isSensitiveKey(key)) {
      sanitized[key] = '[REDACTED]';
      continue;
    }

    // Sanitize string values
    if (typeof value === 'string') {
      sanitized[key] = containsPHI(value) ? sanitizeText(value) : value;
    }
    // Recursively sanitize nested objects
    else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key] = sanitizeProperties(value as Record<string, unknown>);
    }
    // Sanitize arrays
    else if (Array.isArray(value)) {
      sanitized[key] = value.map(item =>
        typeof item === 'string' && containsPHI(item)
          ? sanitizeText(item)
          : typeof item === 'object' && item !== null
          ? sanitizeProperties(item as Record<string, unknown>)
          : item
      );
    }
    // Pass through other types
    else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

// ============================================================================
// User Property Sanitization
// ============================================================================

/**
 * Sanitize user properties for analytics identify
 * Keep only non-PHI properties safe for tracking
 */
export function sanitizeUserProperties(user: Record<string, unknown>): Record<string, unknown> {
  const safeProperties: Record<string, unknown> = {};

  // Allowed properties (non-PHI)
  const allowedKeys = [
    'id',
    'role',
    'organization_id',
    'organization_name',
    'account_type',
    'subscription_plan',
    'created_at',
    'last_login',
    'feature_flags',
    'preferences',
  ];

  for (const key of allowedKeys) {
    if (user[key] !== undefined) {
      safeProperties[key] = user[key];
    }
  }

  // Hash email to create anonymous identifier
  if (user.email && typeof user.email === 'string') {
    safeProperties.email_hash = hashEmail(user.email);
  }

  return safeProperties;
}

/**
 * Simple hash function for email anonymization
 */
function hashEmail(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    const char = email.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

// ============================================================================
// DOM Element Masking
// ============================================================================

/**
 * Mark DOM elements as sensitive (for session replay masking)
 * Usage: Add data-sensitive attribute to input fields
 */
export function markSensitiveElements() {
  // Auto-detect and mark sensitive input fields
  const sensitiveSelectors = [
    'input[type="password"]',
    'input[type="ssn"]',
    'input[name*="ssn"]',
    'input[name*="social"]',
    'input[name*="phone"]',
    'input[name*="email"]',
    'input[name*="dob"]',
    'input[name*="birth"]',
    'input[name*="medicaid"]',
    'input[name*="medicare"]',
    'input[name*="medical"]',
    'input[name*="address"]',
    'input[name*="credit"]',
    'input[name*="card"]',
    '.phi-data',
    '.pii-data',
    '.sensitive-data',
  ];

  sensitiveSelectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
      element.setAttribute('data-sensitive', 'true');
    });
  });
}

/**
 * Initialize HIPAA compliance on page load
 */
export function initializeHIPAACompliance() {
  // Mark sensitive elements on page load
  if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', markSensitiveElements);
    } else {
      markSensitiveElements();
    }

    // Re-mark after DOM mutations (e.g., React renders)
    const observer = new MutationObserver(markSensitiveElements);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

// ============================================================================
// HIPAA Compliance Checklist
// ============================================================================

export const HIPAA_COMPLIANCE_CHECKLIST = {
  datadog: {
    rum: 'Enable RUM with defaultPrivacyLevel: mask-user-input',
    sessionReplay: 'Session replay automatically masks input fields',
    encryption: 'All data encrypted in transit and at rest',
    access: 'Implement role-based access to Datadog dashboards',
    baa: 'Sign BAA with Datadog if required',
  },
  tracking: {
    sanitization: 'Sanitize all tracked properties using sanitizeProperties()',
    userProperties: 'Only track non-PHI user properties',
    eventNames: 'Use generic event names (e.g., "form_submitted" not "patient_registered")',
    sessionReplay: 'Mask all input fields with data-sensitive attribute',
  },
  backend: {
    encryption: 'Encrypt feedback data in database',
    accessLogs: 'Maintain audit logs for all data access',
    authentication: 'Require authentication for all endpoints',
    authorization: 'Implement role-based authorization',
  },
  plain: {
    encryption: 'Enable encryption for all messages',
    retention: 'Configure message retention policy',
    access: 'Limit admin access to super_admin and org_admin only',
    baa: 'Sign BAA with Plain if needed',
  },
};

export default {
  containsPHI,
  sanitizeText,
  sanitizeProperties,
  sanitizeUserProperties,
  markSensitiveElements,
  initializeHIPAACompliance,
  HIPAA_COMPLIANCE_CHECKLIST,
};
